import React from 'react';
import { GlassCard } from './GlassCard';

interface StepCardProps {
  number: number;
  title: string;
  description: React.ReactNode;
  variant?: 'default' | 'compact' | 'timeline';
  isLast?: boolean;
  className?: string;
  animationDelay?: number;
}

export const StepCard: React.FC<StepCardProps> = ({
  number,
  title,
  description,
  variant = 'default',
  isLast = false,
  className = '',
  animationDelay = 0,
}) => {
  if (variant === 'timeline') {
    return (
      <div className={`relative flex gap-4 ${className}`}>
        {/* Timeline connector */}
        {!isLast && (
          <div className="absolute left-6 top-16 bottom-0 w-0.5 bg-gradient-to-b from-primary-light/50 to-transparent dark:from-primary-dark/50" />
        )}

        {/* Number badge */}
        <div
          className={`
            flex-shrink-0 w-12 h-12 rounded-xl
            bg-gradient-to-br from-primary-light to-secondary-light
            dark:from-primary-dark dark:to-secondary-dark
            text-white font-bold text-lg
            flex items-center justify-center
            shadow-lg shadow-primary-light/30 dark:shadow-primary-dark/30
            animate-float
          `}
          style={{ animationDelay: `${animationDelay}ms` }}
        >
          {number}
        </div>

        {/* Content */}
        <div className="flex-1 pb-8">
          <h3 className="text-lg font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
            {title}
          </h3>
          <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
            {description}
          </p>
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <div
          className={`
            flex-shrink-0 w-8 h-8 rounded-lg
            bg-gradient-to-br from-primary-light to-secondary-light
            dark:from-primary-dark dark:to-secondary-dark
            text-white font-bold text-sm
            flex items-center justify-center
          `}
        >
          {number}
        </div>
        <div>
          <span className="font-semibold text-text-primary-light dark:text-text-primary-dark">
            {title}
          </span>
          {description && (
            <span className="text-text-secondary-light dark:text-text-secondary-dark text-sm ml-2">
              — {description}
            </span>
          )}
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <GlassCard variant="medium" hover className={`text-center h-full ${className}`}>
      <div
        className={`
          mx-auto w-16 h-16 rounded-2xl
          bg-gradient-to-br from-primary-light to-secondary-light
          dark:from-primary-dark dark:to-secondary-dark
          text-white
          flex items-center justify-center mb-6
          text-2xl font-bold
          shadow-lg shadow-primary-light/30 dark:shadow-primary-dark/30
          animate-float
        `}
        style={{ animationDelay: `${animationDelay}ms` }}
      >
        {number}
      </div>
      <h3 className="text-xl font-bold mb-3 text-text-primary-light dark:text-text-primary-dark">
        {title}
      </h3>
      <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
        {description}
      </p>
    </GlassCard>
  );
};

export default StepCard;
